import { useEffect, useMemo, useRef, useState } from 'react';

import { Text } from '@/components/shared/components/Text';

interface StreamingResponseProps {
  hexColor: string;
  paragraphs?: string[];
  stream?: boolean;
  loading?: boolean;
  delayMs?: number;
  loadingText?: React.ReactNode | string;
}

export const StreamingResponse = ({
  hexColor,
  paragraphs = [],
  stream = true,
  loading = false,
  delayMs = 35,
  loadingText = <Text tId="LOADING" />,
}: StreamingResponseProps) => {
  const [streamedParagraphs, setStreamedParagraphs] = useState<string[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!paragraphs || paragraphs.length === 0) {
      return;
    }

    setStreamedParagraphs([]);

    const streamParagraphs = (paragraphIndex: number, wordIndex: number) => {
      if (paragraphIndex >= paragraphs.length) return;

      const currentParagraph = paragraphs[paragraphIndex];
      const words = currentParagraph.split(' ');

      if (wordIndex < words.length) {
        // Add current word to the current paragraph
        setStreamedParagraphs((prev) => {
          const newParagraphs = [...prev];
          if (!newParagraphs[paragraphIndex]) {
            newParagraphs[paragraphIndex] = '';
          }
          newParagraphs[paragraphIndex] = newParagraphs[paragraphIndex] + (wordIndex > 0 ? ' ' : '') + words[wordIndex];
          return newParagraphs;
        });

        timeoutRef.current = setTimeout(() => {
          streamParagraphs(paragraphIndex, wordIndex + 1);
        }, delayMs);
      } else {
        // Move to next paragraph
        timeoutRef.current = setTimeout(() => {
          streamParagraphs(paragraphIndex + 1, 0);
        }, delayMs);
      }
    };

    streamParagraphs(0, 0);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [paragraphs, delayMs]);

  const waveColor = useMemo(() => rgba(hexColor), [hexColor]);

  if (loading) {
    return (
      <span className="wave-text" style={{ '--wave-color': waveColor } as React.CSSProperties}>
        {loadingText}
      </span>
    );
  }

  if (!stream) {
    return (
      <span>
        {paragraphs.map((paragraph, index) => (
          <span key={index} style={{ display: 'block', marginBottom: '6px' }}>
            {paragraph}
          </span>
        ))}
      </span>
    );
  }

  return (
    <span>
      {streamedParagraphs.map((paragraph, index) => (
        <span key={index} style={{ display: 'block', marginBottom: '6px' }}>
          {paragraph}
        </span>
      ))}
    </span>
  );
};

const rgba = (hex: string): string => {
  // Remove the '#' if included
  hex = hex.replace('#', '');

  // Expand 3-digit hex to 6-digit hex
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  }

  // Match the 6-digit hex and convert to RGB
  const [r, g, b] = hex.match(/\w\w/g)?.map((x) => parseInt(x, 16)) ?? [];
  return `${r}, ${g}, ${b}`;
};
